import { useEffect, useState } from 'react'
import { Button, LinkButton } from '@/atoms/Button'
import { LegacyImage as Image } from '@/atoms/LegacyImage'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { HeadingXS, ParagraphSM } from '@/atoms/Text'
import { CaptionSM, Text } from '@/atoms/Text/Text'
import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { NextSeoPropsWithRequiredFields, Seo, getBasicPageMetaData } from '@/layout/Seo'
import { DefaultLayout } from '@/layouts/DefaultLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { CloudinaryUploadWidget } from '@/organisms/CloudinaryUploadWidget/CloudinaryUploadWidget'
import { SocialSharing } from '@/organisms/SocialSharing/SocialSharingSOFLargeButtonExperiment'
import { logger } from '@/utils/logging'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { getGenericStaticPaths } from '@/utils/nextUtils/staticPages'
import { loadTranslations } from '@/utils/translate/translate-server'

interface SocialProfileImageCreationPageProps {
  meta: NextSeoPropsWithRequiredFields
}

export const SocialProfileImageCreationPage: NextPageWithLayout<SocialProfileImageCreationPageProps> = ({ meta }) => {
  const [imageUrl, setImageUrl] = useState('')
  const [imagePreview, setImagePreview] = useState('')

  useEffect(() => {
    if (imageUrl !== '') {
      const cloudinaryPath = imageUrl.replace(/https:\/\/images.angelstudios.com\/image\/upload\//, '')
      setImagePreview(cloudinaryPath)
    }
  }, [imageUrl])

  const handleDownload = async () => {
    if (imageUrl !== '') {
      const downloadUrl = `https://images.angelstudios.com/image/upload/f_auto,c_limit,w_384,q_auto/t_social-sharing-overlay/${imageUrl}`
      try {
        const response = await fetch(downloadUrl)
        const blob = await response.blob()
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = 'sound-of-freedom-avatar'
        link.click()
      } catch (error) {
        logger().error('Failed to download the social profile image', { downloadUrl, imageUrl }, error)
      }
    }
  }

  return (
    <>
      <Seo {...meta} />
      <div className="h-full">
        <div className="relative h-[450px] w-full md:h-[550px] xl:h-[750px] ">
          <div className="block md:hidden">
            <Image
              src="c_fill,g_custom,h_600,w_768/b_rgb:fdf4eb,c_pad,g_south,h_1150,w_768/v1684356460/angel-studios/sound-of-freedom/SOF_App-Highlight_4800x2057_20230516_BP.jpg"
              alt="Sound of Freedom banner"
              layout="fill"
              objectFit="cover"
            />
          </div>
          <div className="hidden md:block">
            <Image
              src="/v1690578059/Trailer_Background.png"
              alt="Sound of Freedom banner"
              layout="fill"
              objectFit="cover"
            />
          </div>
          <div className="absolute left-1/2 top-20 h-[111px] w-[311px] -translate-x-1/2 md:hidden">
            <Image
              src="co_rgb:000000,e_colorize:100/v1684270092/angel-app/sound-of-freedom/discovery_images/SOF-light.svg"
              alt="Sound of Freedom banner"
              layout="fill"
              objectFit="contain"
            />
          </div>
          <div className="absolute bottom-0 h-1/4 w-full bg-gradient-to-t from-core-gray-950 md:h-1/2" />
        </div>
        {imageUrl === '' ? (
          <div className="relative z-10 -mt-[150px] flex  flex-col items-center py-10 lg:-mt-[275px] lg:flex-row lg:justify-center lg:gap-10 lg:py-16">
            <div className="mx-auto lg:mx-0 ">
              <Image src="v1689352971/social-preview-2.png" width={260} height={260} alt="example profile image" />
            </div>
            <div className="my-5 p-4 md:max-w-md lg:max-w-2xl">
              <Text
                weight="bold"
                color="white"
                className="photon-title-lg md:photon-heading-sm block text-center lg:text-left"
              >
                Add a God&apos;s Children Are Not For Sale Ring to your Profile
              </Text>
              <ParagraphSM color="white" className="my-4 text-center lg:text-left">
                Upload your profile picture and we will apply the new Sound of Freedom frame to your picture for you to
                download and share on all your social media platforms.
              </ParagraphSM>
              <div className="flex justify-around gap-5 md:justify-start">
                <CloudinaryUploadWidget getImage={setImageUrl} />
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="relative z-10 -mt-[150px] flex  flex-col items-center py-10 lg:-mt-[200px] lg:flex-row lg:justify-center lg:gap-10 lg:py-16">
              <div className="mx-auto lg:mx-0 ">
                <div className="flex overflow-hidden rounded-full">
                  <Image
                    width={260}
                    height={260}
                    src={`/t_social-sharing-overlay/${imagePreview}`}
                    alt="Social Profile"
                  />
                </div>
              </div>
              <div className="my-5 p-4 md:max-w-md lg:max-w-2xl">
                <Text
                  weight="bold"
                  color="white"
                  className="photon-title-md md:photon-heading-xs my-2 block text-center lg:text-left"
                >
                  Share On All Your Social Media Platforms #GodsChildrenAreNotForSale
                </Text>
                <div className="flex flex-col justify-between md:flex-row md:justify-start md:gap-5">
                  <LinkButton
                    variant="white"
                    className="my-2 rounded-lg py-3 lg:hidden"
                    href={`https://images.angelstudios.com/image/upload/f_auto,c_limit,w_384,q_auto/t_social-sharing-overlay/${imageUrl}`}
                    target="_blank"
                  >
                    Download
                  </LinkButton>
                  <Button variant="white" onClick={handleDownload} className="hidden rounded-lg py-3 lg:block">
                    Download
                  </Button>
                  <CloudinaryUploadWidget getImage={setImageUrl} btnText="Create Another Social Frame" />
                </div>
              </div>
            </div>
            <section className="bg-core-gray-900 py-10">
              <PaddedContainer className="text-center">
                <HeadingXS color="white" weight="bold">
                  Share This With Your Friends And Family
                </HeadingXS>
                <CaptionSM color="white" className="mx-auto my-4 max-w-md">
                  Together, we reach every corner of the world. Click below to share the movie with family & friends and
                  see the impact of your share.
                </CaptionSM>
                <div className="mx-auto my-4 max-w-lg">
                  <SocialSharing color="white" url="https://www.angel.com/share/sound-of-freedom" />
                </div>
              </PaddedContainer>
            </section>
          </>
        )}
      </div>
    </>
  )
}

SocialProfileImageCreationPage.getLayout = (page) => {
  return (
    <DefaultLayout isDarkMode={true} siteNavTransparent={true}>
      {page}
    </DefaultLayout>
  )
}

export const getStaticProps = getStaticPropsErrorHandler<SocialProfileImageCreationPageProps>(
  { filePath: 'pages/[locale]/share/sound-of-freedom.tsx' },
  async ({ locale }) => {
    const [meta, translations] = await Promise.all([
      getBasicPageMetaData({
        cloudinaryImagePath: 'v1684790162/angel-studios/tickets/SOF_SEO-Image_1200x630_20230510_BP.jpg',
        cloudinaryTransformation: 'b_rgb:000000',
        description: `Create your own "God's Children Are Not For Sale" social media frame.`,
        locale,
        path: paths.share.soundOfFreedom,
        title: `God's Children Frame | Angel`,
      }),
      loadTranslations(locale, ['common', 'home', 'account']),
    ])

    return {
      props: {
        meta,
        ...translations,
      },
      revalidate: Durations.ONE_DAY_IN_SECONDS,
    }
  },
)

export const getStaticPaths = getGenericStaticPaths

export default SocialProfileImageCreationPage
